import { useEffect, useState } from "react";
import { masterApi } from "@/lib/api";
import { modal } from "@/lib/modal";
import { Switch } from "@/components/ui/switch";
import { Send, MessageSquareText, MessageCircle, CheckCircle2, AlertTriangle, Megaphone } from "lucide-react";

/**
 * Pedido de 26/10/2026 — Master > Mensagens automáticas > Envio das mensagens.
 * Para cada mensagem automática: liga/desliga do envio, rota (SMS ou WhatsApp) e reserva (se a rota
 * escolhida falhar, tenta a outra). Os códigos (OTP) não têm liga/desliga: saem sempre que o
 * cadastro ou o acesso pedem o código; ali só se escolhe a rota. Mostra também se o WhatsApp
 * oficial consegue enviar cada uma (template aprovado ou não) e o liga/desliga das promoções
 * pelo WhatsApp oficial (campanhas e saudade, cobradas como marketing).
 * Contrato: GET/PUT /master/mensagens/envio e PUT /master/mensagens/envio/marketing.
 */

export const ROTA_LBL = { sms: "SMS", whatsapp: "WhatsApp" };
const ROTAS = ["whatsapp", "sms"];

export function outraRota(rota) {
  return rota === "sms" ? "whatsapp" : "sms";
}

/** Frase de como a mensagem sai hoje (para a linha e para a confirmação). */
export function resumoEnvio(item) {
  if (!item) return "";
  if (item.tem_ativo && !item.ativo) return "Desligada: não é enviada.";
  const rota = ROTA_LBL[item.rota] || item.rota;
  return item.reserva
    ? `Sai por ${rota}; se não entregar, por ${ROTA_LBL[outraRota(item.rota)]}.`
    : `Sai só por ${rota}.`;
}

function erroApi(e, padrao = "Não foi possível salvar. Tente de novo.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  return padrao;
}

export default function EnvioMensagens() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState("");

  // Falha ao carregar: nada de provedor/promoções na tela (seria um estado falso), só o aviso e o
  // botão de tentar de novo.
  const carregar = async () => {
    setErro("");
    try {
      const { data } = await masterApi.get("/master/mensagens/envio");
      setDados(data || { itens: [] });
    } catch (e) {
      setDados(null);
      setErro(erroApi(e, "Não foi possível carregar a configuração de envio."));
    }
  };
  useEffect(() => { carregar(); }, []);

  const aplicar = async (item, mudanca, titulo, depois) => {
    const ok = await modal.confirm({
      title: titulo,
      message: "A mudança vale para os próximos envios, em todas as unidades.",
      details: [
        { label: "Mensagem", value: item.rotulo },
        { label: "Antes", value: resumoEnvio(item) },
        { label: "Depois", value: resumoEnvio({ ...item, ...mudanca }) },
        ...(depois ? [{ label: "Atenção", value: depois }] : []),
      ],
      confirmText: "Salvar",
    });
    if (!ok) return;
    setSalvando(item.chave);
    try {
      const { data } = await masterApi.put("/master/mensagens/envio", { chave: item.chave, ...mudanca });
      setDados(data);
      modal.success("Envio atualizado.");
    } catch (e) {
      modal.error(erroApi(e));
    } finally { setSalvando(""); }
  };

  const avisoRota = (item, rota) => {
    if (rota === "whatsapp" && item.whatsapp && !item.whatsapp.disponivel) {
      return `${item.whatsapp.observacao} ${item.reserva ? "A reserva manda por SMS." : "Sem a reserva, a mensagem não sai."}`;
    }
    if (rota === "sms" && item.sms && !item.sms.disponivel) return item.sms.observacao;
    return "";
  };

  const liberarMarketing = async (liberar) => {
    const ok = await modal.confirm({
      title: liberar ? "Liberar promoções pelo WhatsApp oficial" : "Bloquear promoções pelo WhatsApp oficial",
      message: liberar
        ? "Campanhas e cupom de saudade passam a sair pelo WhatsApp oficial com o texto padrão aprovado pela Meta. "
          + "A Meta cobra cada mensagem como marketing (cerca de R$ 0,32) e exige que o cliente tenha aceitado receber promoções. "
          + "As promoções usam no máximo 70% do limite diário do WhatsApp oficial; o restante fica para os códigos e avisos."
        : "Campanhas e cupom de saudade deixam de sair pelo WhatsApp oficial (o SMS da campanha continua).",
      details: [
        { label: "Template das campanhas", value: `${dados?.template_campanha?.nome || "campanha_promocoes"} (${(dados?.template_campanha?.status || "não cadastrado").toLowerCase()})` },
      ],
      confirmText: liberar ? "Liberar" : "Bloquear",
    });
    if (!ok) return;
    setSalvando("marketing");
    try {
      const { data } = await masterApi.put("/master/mensagens/envio/marketing", { liberado: !!liberar });
      setDados(data);
    } catch (e) {
      modal.error(erroApi(e));
    } finally { setSalvando(""); }
  };

  const itens = Array.isArray(dados?.itens) ? dados.itens : [];
  return (
    <section className="card-soft rounded-xl p-3.5 space-y-3" data-testid="envio-secao">
      <div className="flex items-start gap-2">
        <Send size={16} className="text-[color:var(--brand-blue)] mt-0.5 shrink-0" />
        <div>
          <h2 className="font-serif-display text-lg leading-tight">Envio das mensagens</h2>
          <p className="text-[11.5px] text-neutral-500 mt-0.5">
            Liga ou desliga cada mensagem e escolhe por onde ela sai: SMS ou WhatsApp. Com a reserva ligada, se a rota
            escolhida não entregar (serviço fora do ar, sem crédito, template não aprovado), o sistema tenta a outra.
          </p>
          {dados && (
            <p className="text-[11px] text-neutral-500 mt-1" data-testid="envio-provedor">
              WhatsApp em uso: <b>{dados.whatsapp_oficial ? "API oficial da Meta" : "provedor não oficial"}</b>.
            </p>
          )}
        </div>
      </div>
      {erro && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800 flex flex-wrap items-center justify-between gap-2">
          <span data-testid="envio-erro">{erro}</span>
          <button type="button" className="btn-ghost h-8 px-3 text-[11.5px] font-medium" data-testid="envio-recarregar"
                  onClick={carregar}>Tentar novamente</button>
        </div>
      )}
      {dados === null && !erro && <div className="text-[12px] text-neutral-500 py-2">Carregando...</div>}

      <div className="divide-y divide-neutral-100">
        {itens.map((item) => {
          const ocupado = salvando === item.chave;
          const desligado = item.tem_ativo && !item.ativo;
          return (
            <div key={item.chave} className="py-2.5 space-y-1.5" data-testid={`envio-item-${item.chave}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold">{item.rotulo}</div>
                  <div className="text-[10.5px] text-neutral-500">{item.descricao}</div>
                </div>
                {item.tem_ativo ? (
                  <label className="flex items-center gap-2 text-[11px] text-neutral-600 shrink-0">
                    {item.ativo ? "Ligada" : "Desligada"}
                    <Switch data-testid={`envio-ativo-${item.chave}`} checked={!!item.ativo} disabled={ocupado}
                            onCheckedChange={(v) => aplicar(item, { ativo: !!v }, v ? "Ligar o envio" : "Desligar o envio")} />
                  </label>
                ) : (
                  <span className="chip chip-gray text-[10px] py-0.5 shrink-0" data-testid={`envio-sempre-${item.chave}`}>Sai sempre que pedido</span>
                )}
              </div>
              <div className={`flex flex-wrap items-center gap-2 ${desligado ? "opacity-50" : ""}`}>
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">Rota</span>
                <div className="inline-flex rounded-lg border border-neutral-200 overflow-hidden" role="group">
                  {ROTAS.map((r) => {
                    const Icone = r === "sms" ? MessageSquareText : MessageCircle;
                    const marcado = item.rota === r;
                    return (
                      <button key={r} type="button" data-testid={`envio-rota-${item.chave}-${r}`} aria-pressed={marcado}
                              disabled={ocupado || desligado || marcado}
                              onClick={() => aplicar(item, { rota: r }, `Enviar por ${ROTA_LBL[r]}`, avisoRota(item, r))}
                              className={`h-8 px-3 text-[11.5px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                                marcado ? "bg-[color:var(--brand-blue)] text-white" : "bg-white text-neutral-700 hover:bg-neutral-50"}`}>
                        <Icone size={12} /> {ROTA_LBL[r]}
                      </button>
                    );
                  })}
                </div>
                <label className="flex items-center gap-2 text-[11px] text-neutral-600 ml-1">
                  <Switch data-testid={`envio-reserva-${item.chave}`} checked={!!item.reserva} disabled={ocupado || desligado}
                          onCheckedChange={(v) => aplicar(item, { reserva: !!v }, v ? "Ligar a reserva" : "Desligar a reserva")} />
                  Se falhar, enviar por {ROTA_LBL[outraRota(item.rota)]}
                </label>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10.5px]">
                <span data-testid={`envio-wa-${item.chave}`}
                      className={`inline-flex items-start gap-1 ${item.whatsapp?.disponivel ? "text-emerald-700" : "text-amber-700"}`}>
                  {item.whatsapp?.disponivel ? <CheckCircle2 size={11} className="mt-0.5" /> : <AlertTriangle size={11} className="mt-0.5" />}
                  {item.whatsapp?.observacao}
                </span>
                <span data-testid={`envio-sms-${item.chave}`}
                      className={`inline-flex items-start gap-1 ${item.sms?.disponivel ? "text-emerald-700" : "text-amber-700"}`}>
                  {item.sms?.disponivel ? <CheckCircle2 size={11} className="mt-0.5" /> : <AlertTriangle size={11} className="mt-0.5" />}
                  {item.sms?.observacao}
                </span>
              </div>
              {item.observacao && <div className="text-[10.5px] text-neutral-500">{item.observacao}</div>}
            </div>
          );
        })}
      </div>

      {dados && (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50/70 p-3 flex flex-wrap items-start justify-between gap-3" data-testid="envio-marketing">
          <div className="min-w-0 flex-1 flex items-start gap-2">
            <Megaphone size={14} className="text-[color:var(--brand-blue)] mt-0.5 shrink-0" />
            <div>
              <div className="text-[12.5px] font-semibold">Promoções pelo WhatsApp oficial</div>
              <div className="text-[10.5px] text-neutral-500">
                Campanhas e cupom de saudade pela API oficial da Meta. As campanhas saem com o texto padrão aprovado:
                "Olá NOME, temos novas promoções disponíveis no seu cadastro, volte ao UNIDADE e aproveite. Estamos lhe
                esperando!". Template {dados.template_campanha?.nome}:{" "}
                <b data-testid="envio-template-campanha">{(dados.template_campanha?.status || "não cadastrado").toLowerCase()}</b>.
                {dados.marketing_por_env && " Liberado também pela configuração do servidor."}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-neutral-600 shrink-0">
            {dados.marketing_liberado ? "Liberadas" : "Bloqueadas"}
            <Switch data-testid="envio-marketing-switch" checked={!!dados.marketing_liberado}
                    disabled={salvando === "marketing" || dados.marketing_por_env}
                    onCheckedChange={(v) => liberarMarketing(!!v)} />
          </label>
        </div>
      )}
    </section>
  );
}
